import React from 'react';
import { MultiSelectInputProps, Option } from 'framework/form';
import { Badge } from 'framework/ui/mui-builder';
import {
  VACANCY_POSITION_SLOT_WORKER_TYPE_LABELS,
  VACANCY_POSITION_SLOT_STAFF_CONTRACT_TYPES,
  VACANCY_POSITION_SLOT_CONTRACTOR_CONTRACT_TYPES,
  VACANCY_POSITION_SLOT_OFFICE_HIRING_LOCATION,
  VACANCY_POSITION_SLOT_STATUS,
} from './constants';
import { VacancyPositionSlotListItem } from './types';
import {
  COMPETENCY_LEVEL,
  INIT_COMPETENCY_LEVEL,
  VACANCY_POSITION_SLOT_WORKER_TYPE,
} from 'domain/shared/newVacancy/constants';
import { VacancyFormType } from 'domain/shared/newVacancy';

export type PositionSlotOption = Option<string> & {
  positionSlot: VacancyPositionSlotListItem;
};

type VacancyWorkingCondition = VacancyFormType['workingConditions'][number];
export type WorkingConditionFormValue = Partial<Omit<VacancyWorkingCondition, 'contractTypes' | 'employmentTypes'>> & {
  employmentTypes?: string[];
  contractTypes?: string[];
};

const normalizeCompetencyLevel = (competencyLevel?: string | null) =>
  competencyLevel === null ? INIT_COMPETENCY_LEVEL.N_A : competencyLevel;

const buildStatusLabel = (positionSlot: VacancyPositionSlotListItem) => {
  return `${positionSlot.status.charAt(0)}${positionSlot.status.slice(1).toLowerCase()}`;
};

const buildOptionLabel = (positionSlot: VacancyPositionSlotListItem) => {
  const category = VACANCY_POSITION_SLOT_WORKER_TYPE_LABELS[positionSlot.workerType] || positionSlot.workerType;

  return `${positionSlot.positionDisplayName} | ${positionSlot.competencyLevel || COMPETENCY_LEVEL.N_A} | ${
    positionSlot.businessFunctionLabel
  } | ${category} | ${positionSlot.officesLabel} (${positionSlot.id})`;
};

export const mapPositionSlotsToOptions = (positionSlots: VacancyPositionSlotListItem[]): PositionSlotOption[] =>
  positionSlots.map(positionSlot => ({
    value: positionSlot.id,
    label: buildOptionLabel(positionSlot),
    positionSlot,
  }));

export const mapPositionSlotResultsToOptions: NonNullable<MultiSelectInputProps['mapResults']> = results =>
  mapPositionSlotsToOptions(results as unknown as VacancyPositionSlotListItem[]);

export const formatPositionSlotOptionLabel: NonNullable<MultiSelectInputProps['formatOptionLabel']> = option => {
  const positionSlotOption = option as PositionSlotOption;
  const { positionSlot } = positionSlotOption;
  const isFilled = positionSlot.status === VACANCY_POSITION_SLOT_STATUS.FILLED;

  return React.createElement(
    'div',
    { style: { display: 'flex', alignItems: 'center', gap: 8 } },
    React.createElement('span', null, option.label),
    React.createElement(
      Badge,
      { color: isFilled ? 'default' : 'success', sx: { flexShrink: 0, marginLeft: 0 } },
      buildStatusLabel(positionSlot),
    ),
    isFilled && positionSlot.employeeAliases.length
      ? React.createElement('span', null, `by ${positionSlot.employeeAliases.join(', ')}`)
      : null,
  );
};

export const buildDefaultWorkingConditions = (): WorkingConditionFormValue[] => [
  { hiringLocation: '', employmentTypes: [], contractTypes: [], isRelocationAvailable: '' },
];

export const buildPositionSlotFormData = (positionSlot?: VacancyPositionSlotListItem) => {
  if (!positionSlot) {
    return {
      positionSlotScope: null,
      specialization: null,
      technology: null,
      competencyLevel: null,
      businessFunctionId: null,
      workerType: null,
    };
  }

  const competencyLevel = normalizeCompetencyLevel(positionSlot.competencyLevel);

  return {
    positionSlotScope: {
      specializationId: positionSlot.specializationId,
      technologyId: positionSlot.technologyId,
      competencyLevel,
      businessFunctionId: positionSlot.businessFunctionId,
      workerType: positionSlot.workerType,
      officeIds: positionSlot.officeIds,
    },
    specialization: positionSlot.specialization,
    technology: positionSlot.technology,
    competencyLevel,
    businessFunctionId: positionSlot.businessFunctionId,
    workerType: positionSlot.workerType,
  };
};

export const buildPositionSlotWorkingConditionsData = (
  workingConditions: WorkingConditionFormValue[] | undefined,
  positionSlot?: VacancyPositionSlotListItem,
) => {
  if (!workingConditions || !positionSlot) {
    return {};
  }

  const hiringLocations = (positionSlot.officeIds || [])
    .map(officeId => VACANCY_POSITION_SLOT_OFFICE_HIRING_LOCATION[officeId])
    .filter(Boolean);

  const buildWorkingCondition = (
    workingCondition: WorkingConditionFormValue,
    hiringLocation?: string,
  ): WorkingConditionFormValue => ({
    ...workingCondition,
    ...(hiringLocation ? { hiringLocation } : {}),
  });
  const positionSlotWorkingConditions = hiringLocations.length
    ? hiringLocations.map((hiringLocation, index) =>
        buildWorkingCondition(workingConditions[index] || workingConditions[0] || {}, hiringLocation),
      )
    : workingConditions.map(workingCondition => buildWorkingCondition(workingCondition));

  if (positionSlot.workerType === VACANCY_POSITION_SLOT_WORKER_TYPE.STAFF) {
    return {
      workingConditions: positionSlotWorkingConditions.map(workingCondition => ({
        ...workingCondition,
        contractTypes: VACANCY_POSITION_SLOT_STAFF_CONTRACT_TYPES,
      })),
    };
  }

  if (positionSlot.workerType === VACANCY_POSITION_SLOT_WORKER_TYPE.CONTRACTOR) {
    return {
      workingConditions: positionSlotWorkingConditions.map(workingCondition => ({
        ...workingCondition,
        contractTypes: Array.isArray(workingCondition.contractTypes)
          ? workingCondition.contractTypes.filter((contractType: string) =>
              VACANCY_POSITION_SLOT_CONTRACTOR_CONTRACT_TYPES.includes(contractType),
            )
          : [],
      })),
    };
  }

  return {
    workingConditions: positionSlotWorkingConditions,
  };
};

export const includesSearchValue = (positionSlot: VacancyPositionSlotListItem, searchValue: string) => {
  if (!searchValue) {
    return true;
  }

  const normalizedSearchValue = searchValue.toLowerCase();
  const searchableValues = [
    positionSlot.id,
    positionSlot.positionDisplayName,
    positionSlot.technologyLabel,
    positionSlot.competencyLevel,
    positionSlot.businessFunctionLabel,
    positionSlot.workerType,
    positionSlot.officesLabel,
  ];

  return searchableValues.some(value => value?.toLowerCase().includes(normalizedSearchValue));
};
